import React, { useCallback, useMemo } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import Field from '../../../../../../components/Form/Field'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { formatPastedJavascriptTags } from '../../../helpers'

import { updateAd } from '../../../../../../modules/actions/ads'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { getInitialValues, getValidationSchema } from './fields'
import { THIRD_PARTY_TAG, VAST_TAG_URL } from '../../../fields'
import { DV_360_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

const ThirdPartySectionForm = ({ editItemData: ad, successSubmit, isInternalAd, title, ...formProps }) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const { id, account: adAccountId } = ad
  const adAccount = adAccountId || selectedAdAccountId

  const initialValues = useMemo(() => getInitialValues(ad), [ad])
  const validationSchema = useMemo(() => getValidationSchema(ad), [ad])

  const handleSubmit = useCallback(
    values => {
      dispatch(
        updateAd(
          {
            account: adAccount,
            ...(ad[VAST_TAG_URL]
              ? { [VAST_TAG_URL]: values[VAST_TAG_URL] }
              : { [THIRD_PARTY_TAG]: values[THIRD_PARTY_TAG] }),
            ...(isInternalAd && { internal: true }),
            meta: {
              [HISTORY_META_EDIT_SECTION]: title
            }
          },
          id,
          DV_360_PLATFORM
        )
      )
    },
    [dispatch, id, isInternalAd, adAccount, title, ad]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true
  })

  const purifiedFormik = usePurifiedFormik(formik)

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={successSubmit || manualSuccessSubmit}
      {...formProps}
    >
      {ad[VAST_TAG_URL] ? (
        <Field placeholder={t('VAST Tag')} formik={formik} name={VAST_TAG_URL} isTextarea={true} />
      ) : (
        <Field
          placeholder={t('JavaScript Tag')}
          formik={formik}
          name={THIRD_PARTY_TAG}
          isTextarea={true}
          modifyValueOnBlur={formatPastedJavascriptTags}
        />
      )}
    </EditForm>
  )
}

export default ThirdPartySectionForm
